import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const THEME_BLUE = '#2b5baa';

const s = {
  section: { background: '#0f172a', color: '#e5e7eb', padding: '40px 0 0', borderTop: '1px solid rgba(255,255,255,0.06)' },
  title: { color: '#fff', fontWeight: 600, fontSize: 16, margin: '0 0 10px' },
  tag: { margin: 0, color: '#cbd5e1', lineHeight: 1.5 },
  rowGap: { rowGap: 24 },
  brandWrap: { display: 'flex', alignItems: 'center', gap: 12 },
  logo: { width: 52, height: 52, objectFit: 'contain', borderRadius: 8 },
  list: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 },
  item: { display: 'grid', gridTemplateColumns: '18px 1fr', alignItems: 'start', gap: 8 },
  icon: { color: THEME_BLUE, marginTop: 2 },
  link: { color: '#e5e7eb', textDecoration: 'none' },
  socials: { display: 'flex', gap: 10, marginTop: 10 },
  socialBtn: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', color: '#e5e7eb'
  },
  links: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 },
  linkItem: { display: 'flex', alignItems: 'center', gap: 8 },
  chev: { color: THEME_BLUE, width: 14, textAlign: 'center' },
  divider: { marginTop: 24, borderTop: '1px solid rgba(255,255,255,0.08)' },
  copyright: { textAlign: 'center', color: '#94a3b8', padding: '12px 0' }
};

const Footer = ({
  TAGLINE = "Moving forward with innovative sorting solutions.",
  CONTACT = "CONTACT",
  ADDRESS = "3995 Lesage Sherbrooke (Quebec) J1L 2Z9 Canada",
  QUICK_LINKS = "QUICK LINKS",
  HOME = "Home",
  ABOUT = "About",
  PRODUCTS = "Products",
  PRIVACY = "Privacy Policy",
  CONTACT_PAGE = "Contact",
  COPYRIGHT = "developed by",
}) => {
  const year = new Date().getFullYear();
  const { currentLanguage, translateContent } = useLanguage();
  const [translations, setTranslations] = useState({
    TAGLINE, CONTACT, ADDRESS, QUICK_LINKS, HOME, ABOUT, PRODUCTS, PRIVACY, CONTACT_PAGE, COPYRIGHT,
  });

  useEffect(() => {
    const translateAll = async () => {
      const keys = ['TAGLINE', 'CONTACT', 'ADDRESS', 'QUICK_LINKS', 'HOME', 'ABOUT', 'PRODUCTS', 'PRIVACY', 'CONTACT_PAGE', 'COPYRIGHT'];
      const originalTexts = { TAGLINE, CONTACT, ADDRESS, QUICK_LINKS, HOME, ABOUT, PRODUCTS, PRIVACY, CONTACT_PAGE, COPYRIGHT };

      const newTranslations = {};

      const translationPromises = keys.map(async (key) => {
        const translated = await translateContent(originalTexts[key]);
        return { key, translated };
      });

      const results = await Promise.all(translationPromises);

      results.forEach(({ key, translated }) => {
        newTranslations[key] = translated;
      });

      setTranslations(newTranslations);
    };

    translateAll();
  }, [currentLanguage, TAGLINE, CONTACT, ADDRESS, QUICK_LINKS, HOME, ABOUT, PRODUCTS, PRIVACY, CONTACT_PAGE, COPYRIGHT, translateContent]);

  return (
    <footer style={s.section}>
      <div className="container">
        <div className="row" style={s.rowGap}>
          {/* Brand + tagline */}
          <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
            <div style={s.brandWrap}>
              <div>
                <h6 style={{ ...s.title, margin: 0 }}>Eagle Vizion</h6>
                <p style={s.tag}>{translations.TAGLINE}</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="col-xl-4 col-lg-4 col-md-6 col-sm-12">
            <h6 style={s.title}>{translations.CONTACT}</h6>
            <ul style={s.list}>
              <li style={s.item}>
                <i className="fas fa-map-marker-alt" style={s.icon} aria-hidden="true" />
                <a
                  style={s.link}
                  href="https://maps.google.com/?q=3425%20Bd%20Industriel,%20Sherbrooke,%20QC%20J1L%202W1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {translations.ADDRESS}
                </a>
              </li>
              <li style={s.item}>
                <i className="fas fa-phone" style={s.icon} aria-hidden="true" />
                <a style={s.link} href="tel:+1 819-481-1325">+1 819-481-1325</a>
              </li>
              <li style={s.item}>
                <i className="fas fa-phone" style={s.icon} aria-hidden="true" />
                <a style={s.link} href="tel:+18332732453">+1 833 273-2453</a>
              </li>
              <li style={s.item}>
                <i className="fas fa-envelope" style={s.icon} aria-hidden="true" />
                <a style={s.link} href="mailto:info@eaglevizion.com">info@eaglevizion.com</a>
              </li>
            </ul>
          </div>

          {/* Discover (Quick links) */}
          <div className="col-xl-4 col-lg-4 col-md-12 col-sm-12">
            <h6 style={s.title}>{translations.QUICK_LINKS}</h6>
            <ul style={s.links}>
              <li style={s.linkItem}><span style={s.chev}>&raquo;</span><Link to="/" style={s.link}>{translations.HOME}</Link></li>
              <li style={s.linkItem}><span style={s.chev}>&raquo;</span><Link to="/about" style={s.link}>{translations.ABOUT}</Link></li>
              <li style={s.linkItem}><span style={s.chev}>&raquo;</span><Link to="/products" style={s.link}>{translations.PRODUCTS}</Link></li>
              <li style={s.linkItem}><span style={s.chev}>&raquo;</span><Link to="/Politique_de_confidentialité" style={s.link}>{translations.PRIVACY}</Link></li>
              <li style={s.linkItem}><span style={s.chev}>&raquo;</span><Link to="/contact" style={s.link}>{translations.CONTACT_PAGE}</Link></li>
            </ul>
            <div style={s.socials}>
              <a href="https://fr-ca.facebook.com" target="_blank" rel="noopener noreferrer" style={s.socialBtn} aria-label="Facebook"><i className="fab fa-facebook-f" /></a>
              <a href="https://x.com/?lang=fr" target="_blank" rel="noopener noreferrer" style={s.socialBtn} aria-label="Twitter/X"><i className="fab fa-twitter" /></a>
              <a href="https://www.youtube.com/@eaglevizion1292" target="_blank" rel="noopener noreferrer" style={s.socialBtn} aria-label="YouTube"><i className="fab fa-youtube" /></a>
              <a href="https://www.linkedin.com/company/eagle-vizion/?originalSubdomain=fr" target="_blank" rel="noopener noreferrer" style={s.socialBtn} aria-label="LinkedIn"><i className="fab fa-linkedin" /></a>
            </div>
          </div>
        </div>

        {/* Divider + centered copyright */}
        <div style={s.divider} />
        <div style={s.copyright}>
          © Eagle Vizion {translations.COPYRIGHT} <a href="#" style={s.link}>OEM ENGINEERING</a> - {year}
        </div>
      </div>
    </footer>
  );
};

export default Footer;